import { useTranslation } from 'react-i18next';
import type { CalendarEntry, GlobalEvent, SchoolHoliday, PersonalTask } from '@/mocks/calendarEntries';

interface CalendarGridProps {
  currentMonth: Date;
  entries: CalendarEntry[];
  visibleTypes: { global: boolean; holiday: boolean; task: boolean };
  onDateClick: (date: string) => void;
  onTaskToggle: (taskId: string) => void;
  selectedDate: string | null;
}

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function formatDateStr(year: number, month: number, day: number): string {
  const m = String(month + 1).padStart(2, '0');
  const d = String(day).padStart(2, '0');
  return `${year}-${m}-${d}`;
}

function getEventsForDate(dateStr: string, entries: CalendarEntry[]): CalendarEntry[] {
  const result: CalendarEntry[] = [];
  const parts = dateStr.split('-');
  const ddMM = `${parts[2]}/${parts[1]}`;

  for (const entry of entries) {
    if (entry.type === 'global') {
      if (entry.date === ddMM) result.push(entry);
    } else if (entry.type === 'holiday') {
      const holiday = entry as SchoolHoliday;
      if (dateStr >= holiday.startDate && dateStr <= holiday.endDate) result.push(entry);
    } else if (entry.type === 'task') {
      if (entry.date === dateStr) result.push(entry);
    }
  }
  return result;
}

function isToday(year: number, month: number, day: number): boolean {
  const now = new Date();
  return now.getFullYear() === year && now.getMonth() === month && now.getDate() === day;
}

export default function CalendarGrid({ currentMonth, entries, visibleTypes, onDateClick, onTaskToggle, selectedDate }: CalendarGridProps) {
  const { t } = useTranslation();
  const year = currentMonth.getFullYear();
  const month = currentMonth.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const todayStr = formatDateStr(new Date().getFullYear(), new Date().getMonth(), new Date().getDate());

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const getCellStyle = (day: number | null): string => {
    if (day === null) return '';
    const dateStr = formatDateStr(year, month, day);
    const dayEntries = getEventsForDate(dateStr, entries);
    const hasHoliday = dayEntries.some((e) => e.type === 'holiday' && visibleTypes.holiday);
    if (hasHoliday) return 'bg-red-50 border-red-200';
    return '';
  };

  const getHolidayLabel = (day: number | null): string | null => {
    if (day === null) return null;
    const dateStr = formatDateStr(year, month, day);
    const holidays = entries.filter((e) => e.type === 'holiday' && visibleTypes.holiday);
    for (const h of holidays) {
      const holiday = h as SchoolHoliday;
      if (dateStr >= holiday.startDate && dateStr <= holiday.endDate) return holiday.holidayTitle;
    }
    return null;
  };

  const getGlobalEventLabel = (day: number | null): string | null => {
    if (day === null) return null;
    const dateStr = formatDateStr(year, month, day);
    const ddMM = `${String(day).padStart(2, '0')}/${String(month + 1).padStart(2, '0')}`;
    const global = entries.find((e) => e.type === 'global' && visibleTypes.global && (e as GlobalEvent).date === ddMM);
    return global ? (global as GlobalEvent).eventName : null;
  };

  const rows: (number | null)[][] = [];
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7));
  }

  return (
    <div className="bg-background-50 rounded-xl border border-background-200/70 overflow-hidden">
      {/* Day-of-week headers */}
      <div className="grid grid-cols-7 border-b border-background-200/70">
        {DAYS_OF_WEEK.map((dow) => (
          <div key={dow} className="py-2.5 text-center text-xs font-label font-semibold text-foreground-500 uppercase tracking-wide">
            {dow}
          </div>
        ))}
      </div>

      {/* Calendar grid */}
      <div className="grid grid-cols-7">
        {rows.flat().map((day, idx) => {
          if (day === null) {
            return <div key={`empty-${idx}`} className="min-h-[90px] border-b border-r border-background-100/60 bg-background-50/50" />;
          }
          const dateStr = formatDateStr(year, month, day);
          const dayEntries = getEventsForDate(dateStr, entries).filter((e) => {
            if (e.type === 'global') return visibleTypes.global;
            if (e.type === 'holiday') return visibleTypes.holiday;
            if (e.type === 'task') return visibleTypes.task;
            return true;
          });
          const todayCheck = isToday(year, month, day);
          const isSelected = dateStr === selectedDate;
          const hasHoliday = dayEntries.some((e) => e.type === 'holiday');
          const holidayLabel = getHolidayLabel(day);
          const globalLabel = getGlobalEventLabel(day);
          const tasks = dayEntries.filter((e) => e.type === 'task') as PersonalTask[];

          return (
            <button
              key={dateStr}
              onClick={() => onDateClick(dateStr)}
              className={`min-h-[90px] p-1.5 border-b border-r border-background-100/60 text-left transition-colors cursor-pointer relative group
                ${hasHoliday && visibleTypes.holiday ? 'bg-red-50/70 border-red-200/60' : 'bg-background-50 hover:bg-background-100'}
                ${isSelected ? 'ring-2 ring-primary-400 ring-inset' : ''}
                ${todayCheck ? 'bg-primary-50/50' : ''}
              `}
            >
              <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-label font-semibold
                ${todayCheck ? 'bg-primary-500 text-background-50' : 'text-foreground-700'}
              `}>
                {day}
              </span>

              {/* Holiday banner */}
              {holidayLabel && visibleTypes.holiday && (
                <div className="mt-0.5 px-1 py-0.5 rounded text-[10px] font-label font-medium bg-red-100 text-red-700 truncate leading-tight">
                  {holidayLabel}
                </div>
              )}

              {/* Global event indicator */}
              {globalLabel && visibleTypes.global && (
                <div className="mt-0.5 flex items-center gap-0.5 text-[10px] font-label text-blue-600 truncate leading-tight">
                  <span className="w-1.5 h-1.5 rounded-full bg-blue-500 flex-shrink-0" />
                  <span className="truncate">{globalLabel}</span>
                </div>
              )}

              {/* Task indicators */}
              {visibleTypes.task && tasks.slice(0, 3).map((task) => (
                <button
                  key={task.id}
                  onClick={(e) => { e.stopPropagation(); onTaskToggle(task.id); }}
                  className={`mt-0.5 flex items-center gap-1 w-full text-[10px] font-label text-left rounded px-1 py-0.5 transition-colors cursor-pointer truncate leading-tight
                    ${task.completed ? 'bg-green-50 text-green-600 line-through' : 'bg-green-50 text-green-700 hover:bg-green-100'}
                  `}
                >
                  <i className={`${task.completed ? 'ri-checkbox-circle-fill' : 'ri-checkbox-blank-circle-line'} text-[10px] flex-shrink-0`} />
                  <span className="truncate">{task.title}</span>
                </button>
              ))}
              {visibleTypes.task && tasks.length > 3 && (
                <span className="text-[10px] text-foreground-400 pl-1">+{tasks.length - 3} more</span>
              )}

              {/* Empty state click hint */}
              {dayEntries.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                  <span className="text-[10px] text-foreground-300 bg-background-50/90 px-1.5 py-0.5 rounded">Click to add</span>
                </div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}